import axios from 'axios';

// Utility function to get CSRF token from cookies
function getCSRFToken() {
  const name = 'csrf_token=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return null;
}

// Production-ready token refresh interceptor
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

// Response interceptor for automatic token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if this is a guest-allowed endpoint that should work without auth
      const isGuestAllowedEndpoint = originalRequest.url && (
        originalRequest.url.includes('/api/voice/invitation-details/') ||
        originalRequest.url.includes('/api/voice/join-invite/') ||
        originalRequest.url.includes('/api/auth/verify') ||  // Guest experience should handle auth verification gracefully
        originalRequest.url.includes('/api/health') ||  // Public endpoint
        originalRequest.url.includes('/api/group/validate-invitation') ||  // Invitation validation
        originalRequest.url.includes('/api/group/accept-invitation')  // Invitation acceptance
      );

      // Check if we're on a guest-friendly page that shows feature gates
      const isGuestFriendlyPage = window.location.pathname === '/tutor' ||
                                  window.location.pathname === '/dashboard' ||
                                  window.location.pathname === '/' ||
                                  window.location.pathname === '/signup' ||
                                  window.location.pathname === '/accept-invite' ||
                                  window.location.pathname === '/login' ||
                                  window.location.pathname === '/pricing';

      // Check if we actually have an auth token before attempting refresh
      const hasAuthCookie = document.cookie.includes('auth_token=');
      if (!hasAuthCookie) {
        // For guest-allowed endpoints or guest-friendly pages, allow the 401 to pass through
        // instead of redirecting to login
        if (isGuestAllowedEndpoint || isGuestFriendlyPage) {
          return Promise.reject(error);
        }

        // No auth cookie means user is not logged in - don't attempt refresh
        console.log('No auth cookie found, redirecting to login');
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue requests while refresh is happening
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return axios(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Get CSRF token for secure refresh request
        const csrfToken = getCSRFToken();
        const headers = csrfToken ? { 'X-CSRF-Token': csrfToken } : {};
        
        await axios.post('/api/auth/refresh', {}, { headers });
        processQueue(null);
        return axios(originalRequest); // Retry original request
      } catch (refreshError) {
        processQueue(refreshError);
        // Clear user state and redirect to login
        console.log('Token refresh failed, redirecting to login');
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axios;